import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Search,
    Building2,
    BookmarkPlus,
    Bookmark,
    Trash2,
    Loader2,
    TrendingDown,
    AlertCircle,
    FileBarChart2,
    Hash,
    RefreshCw,
} from "lucide-react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
    searchCompetitors,
    listSavedCompetitors,
    saveCompetitor,
    deleteSavedCompetitor,
    refreshPlcspFeed,
} from "@/lib/api";
import { fmtEUR, fmtDate } from "@/lib/format";
import CompetitorResultsTable from "@/components/competidores/CompetitorResultsTable";

const KPI = ({ label, value, hint, accent }) => (
    <div className="bg-white border border-ink-100 rounded-2xl p-5">
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-400 mb-1.5">
            {label}
        </div>
        <div
            className={`font-mono text-2xl font-bold tracking-tight ${
                accent === "brand"
                    ? "text-brand-600"
                    : accent === "emerald"
                      ? "text-emerald-600"
                      : "text-ink-950"
            }`}
        >
            {value}
        </div>
        {hint && <div className="text-xs text-ink-500 mt-1 font-medium">{hint}</div>}
    </div>
);

export default function Competidores() {
    const [name, setName] = useState("");
    const [cif, setCif] = useState("");
    const [results, setResults] = useState([]);
    const [aggregates, setAggregates] = useState({ contracts: 0 });
    const [cacheWarming, setCacheWarming] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saved, setSaved] = useState([]);
    const [hasSearched, setHasSearched] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    const handleRefresh = async () => {
        setRefreshing(true);
        try {
            const r = await refreshPlcspFeed();
            if (r.started) {
                toast.success(
                    "Refrescando feed PLCSP en segundo plano (30-60 s). Se reintentará la búsqueda automáticamente."
                );
                setCacheWarming(true);
            } else {
                toast.info("Ya hay un refresco en curso");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo refrescar");
        } finally {
            setRefreshing(false);
        }
    };

    const loadSaved = useCallback(async () => {
        try {
            const data = await listSavedCompetitors();
            setSaved(data || []);
        } catch (err) {
            // No bloqueamos la UI si la lista de guardados falla
            // (p. ej. red intermitente); solo log para diagnóstico.
            console.warn("[competidores] loadSaved error:", err?.message || err);
        }
    }, []);
    useEffect(() => {
        loadSaved();
    }, [loadSaved]);

    const doSearch = useCallback(
        async (overrideName, overrideCif) => {
            const n = overrideName ?? name;
            const c = overrideCif ?? cif;
            if (!n.trim() && !c.trim()) {
                toast.error("Introduce un nombre o un CIF para buscar");
                return;
            }
            setLoading(true);
            setHasSearched(true);
            try {
                const data = await searchCompetitors({
                    q: n.trim(),
                    cif: c.trim(),
                });
                setResults(data.results || []);
                setAggregates(data.aggregates || { contracts: 0 });
                setCacheWarming(Boolean(data.cache_warming));
            } catch (e) {
                toast.error(e?.response?.data?.detail || "Error en la búsqueda");
            } finally {
                setLoading(false);
            }
        },
        [name, cif]
    );

    // Poll while cache is warming
    useEffect(() => {
        if (!cacheWarming || !hasSearched) return;
        const id = setInterval(() => doSearch(), 5000);
        return () => clearInterval(id);
    }, [cacheWarming, hasSearched, doSearch]);

    const onSubmit = (e) => {
        e.preventDefault();
        doSearch();
    };

    const save = async () => {
        if (!name.trim() && !cif.trim()) return;
        try {
            const rec = await saveCompetitor({ name: name.trim(), cif: cif.trim() });
            toast.success(`Guardado: ${rec.name || rec.cif}`);
            loadSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo guardar");
        }
    };

    const removeSaved = async (id) => {
        try {
            await deleteSavedCompetitor(id);
            setSaved((s) => s.filter((x) => x.id !== id));
            toast.success("Competidor eliminado");
        } catch {
            toast.error("Error al eliminar");
        }
    };

    const openSaved = (c) => {
        setName(c.name || "");
        setCif(c.cif || "");
        doSearch(c.name || "", c.cif || "");
    };

    const isAlreadySaved = useMemo(() => {
        const nz = (name || "").trim().toLowerCase();
        const cz = (cif || "").trim().toUpperCase().replace(/[^A-Z0-9]/g, "");
        return saved.some(
            (s) =>
                (cz && (s.cif || "").toUpperCase() === cz) ||
                (!cz && nz && (s.name || "").toLowerCase() === nz)
        );
    }, [saved, name, cif]);

    return (
        <Layout>
            <div
                className="max-w-7xl mx-auto px-6 py-10 space-y-6"
                data-testid="competidores-page"
            >
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="inline-flex items-center gap-1.5 bg-brand-50 text-brand-700 px-3 py-1 rounded-full mb-3">
                        <TrendingDown size={12} />
                        <span className="text-[10px] font-bold uppercase tracking-[0.18em]">
                            Inteligencia competitiva
                        </span>
                    </div>
                    <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight text-ink-950">
                        Competidores
                    </h1>
                    <p className="text-ink-500 font-medium mt-2 max-w-3xl">
                        Busca a qué precio fue una empresa en otras licitaciones y a cuánto se
                        bajó. Los datos vienen de la caché en vivo de la Plataforma de
                        Contratación del Sector Público (~7-10 días).
                    </p>
                </div>
                <Button
                    variant="outline"
                    onClick={handleRefresh}
                    disabled={refreshing || cacheWarming}
                    className="rounded-xl"
                    data-testid="refresh-feed-btn"
                >
                    {refreshing || cacheWarming ? (
                        <>
                            <Loader2 size={14} className="mr-1.5 animate-spin" />
                            Refrescando…
                        </>
                    ) : (
                        <>
                            <RefreshCw size={14} className="mr-1.5" />
                            Refrescar feed PLCSP
                        </>
                    )}
                </Button>
            </div>

                {/* Search form */}
                <form
                    onSubmit={onSubmit}
                    className="bg-white border border-ink-100 rounded-2xl p-5 grid md:grid-cols-[1.4fr,1fr,auto,auto] gap-3 items-end"
                    data-testid="competidor-search-form"
                >
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-ink-500 mb-1.5 block">
                            Nombre de la empresa
                        </label>
                        <div className="relative">
                            <Building2
                                size={15}
                                className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400"
                            />
                            <Input
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="J&A Garrigues"
                                className="pl-10 rounded-xl h-11"
                                data-testid="competitor-name-input"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-ink-500 mb-1.5 block">
                            CIF / NIF
                        </label>
                        <div className="relative">
                            <Hash
                                size={15}
                                className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400"
                            />
                            <Input
                                value={cif}
                                onChange={(e) => setCif(e.target.value.toUpperCase())}
                                placeholder="B81709081"
                                className="pl-10 rounded-xl h-11 font-mono uppercase"
                                data-testid="competitor-cif-input"
                            />
                        </div>
                    </div>
                    <Button
                        type="submit"
                        disabled={loading}
                        className="rounded-xl bg-brand-600 hover:bg-brand-700 text-white font-bold h-11 shadow-pop"
                        data-testid="competitor-search-btn"
                    >
                        {loading ? (
                            <>
                                <Loader2 size={14} className="mr-1.5 animate-spin" />
                                Buscando…
                            </>
                        ) : (
                            <>
                                <Search size={14} className="mr-1.5" />
                                Buscar
                            </>
                        )}
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={save}
                        disabled={(!name.trim() && !cif.trim()) || isAlreadySaved}
                        className="rounded-xl h-11"
                        data-testid="competitor-save-btn"
                    >
                        {isAlreadySaved ? (
                            <>
                                <Bookmark size={14} className="mr-1.5" />
                                Guardado
                            </>
                        ) : (
                            <>
                                <BookmarkPlus size={14} className="mr-1.5" />
                                Guardar
                            </>
                        )}
                    </Button>
                </form>

                {/* Saved competitors */}
                {saved.length > 0 && (
                    <div data-testid="saved-competitors">
                        <h3 className="text-xs font-bold uppercase tracking-wider text-ink-500 mb-2">
                            Empresas guardadas
                        </h3>
                        <div className="flex flex-wrap gap-2">
                            {saved.map((c) => (
                                <div
                                    key={c.id}
                                    className="inline-flex items-center gap-1.5 bg-white border border-ink-200 rounded-full pl-3 pr-1 py-1 hover:border-brand-300 transition-colors"
                                >
                                    <button
                                        type="button"
                                        onClick={() => openSaved(c)}
                                        className="text-sm font-bold text-ink-950 hover:text-brand-600"
                                        data-testid={`saved-${c.id}`}
                                    >
                                        {c.name || c.cif}
                                    </button>
                                    {c.cif && (
                                        <span className="text-[10px] font-mono text-ink-400">
                                            {c.cif}
                                        </span>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => removeSaved(c.id)}
                                        className="p-1 text-ink-400 hover:text-red-600 rounded-full"
                                        data-testid={`del-saved-${c.id}`}
                                        title="Eliminar"
                                    >
                                        <Trash2 size={12} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Warming notice */}
                {cacheWarming && (
                    <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-2xl p-4 flex items-start gap-3 font-medium text-sm">
                        <Loader2 size={16} className="animate-spin shrink-0 mt-0.5" />
                        Descargando feed PLCSP por primera vez (60-90 s). Se reintentará
                        automáticamente.
                    </div>
                )}

                {/* Results */}
                {!cacheWarming && hasSearched && (
                    <>
                        {aggregates.contracts > 0 ? (
                            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                <KPI
                                    label="Contratos adjudicados"
                                    value={aggregates.contracts}
                                    accent="brand"
                                />
                                <KPI
                                    label="Total adjudicado"
                                    value={fmtEUR(aggregates.total_awarded || 0)}
                                    hint="IVA excluido"
                                />
                                <KPI
                                    label="Baja media"
                                    value={
                                        aggregates.avg_discount != null
                                            ? `${aggregates.avg_discount} %`
                                            : "—"
                                    }
                                    hint={
                                        aggregates.reliable_discount_count != null
                                            ? `Solo bajas fiables (${aggregates.reliable_discount_count} de ${aggregates.contracts})`
                                            : aggregates.min_discount != null &&
                                                aggregates.max_discount != null
                                              ? `Rango ${aggregates.min_discount}–${aggregates.max_discount} %`
                                              : ""
                                    }
                                    accent="emerald"
                                />
                                <KPI
                                    label="Último contrato"
                                    value={
                                        aggregates.last_award_date
                                            ? fmtDate(aggregates.last_award_date)
                                            : "—"
                                    }
                                    hint={
                                        aggregates.first_award_date &&
                                        `desde ${fmtDate(aggregates.first_award_date)}`
                                    }
                                />
                            </div>
                        ) : (
                            <div
                                className="bg-amber-50 border border-amber-200 text-amber-800 rounded-2xl p-5 flex items-start gap-3 text-sm font-medium"
                                data-testid="no-competitor-results"
                            >
                                <AlertCircle size={18} className="shrink-0 mt-0.5" />
                                <div>
                                    No se han encontrado adjudicaciones a esa empresa en el
                                    feed actual de la PLCSP (últimos ~10 días, ~4.000
                                    licitaciones). Verifica el nombre/CIF o vuelve más tarde
                                    cuando se renueve la caché.
                                </div>
                            </div>
                        )}

                        {/* Results table */}
                        <CompetitorResultsTable results={results} />
                    </>
                )}

                {/* Initial empty state */}
                {!hasSearched && saved.length === 0 && (
                    <div className="bg-white border border-ink-100 rounded-2xl py-12 px-6 text-center">
                        <FileBarChart2
                            size={28}
                            className="text-ink-300 mx-auto mb-3"
                        />
                        <div className="font-display text-base font-bold text-ink-700 mb-1">
                            Empieza buscando una empresa
                        </div>
                        <div className="text-sm text-ink-500 max-w-md mx-auto">
                            Introduce el nombre o CIF y verás todas sus adjudicaciones públicas
                            con el % de baja sobre el presupuesto.
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}
